import {
  useAiDocumentationPreviewQuery,
  AiDocumentationPreviewQuery,
} from 'graphql-common';

export type DocReferenceDocumentation = AiDocumentationPreviewQuery['data'];

export type DocReferenceResult = {
  loading: boolean;
  error: boolean;
  documentation: DocReferenceDocumentation;
};

type Options = {
  // While the assistant turn is still streaming we render a placeholder and do
  // not fetch — avoids firing a query on every delta and on half-formed ids.
  skip?: boolean;
  // The `version-id` the assistant citation pinned (BE guide §5). When set, the
  // chip resolves to that exact version's file/notes instead of the document's
  // current version.
  versionId?: string;
};

// Resolves a `:documentation{id}` reference into the data DocumentationPreview
// needs via the thin `aiDocumentationPreview(id)` query — one network call per
// reference, fetching only the preview fields (name, currentVersion attachment,
// notes, read, favorited) instead of the heavy edmDocumentation payload.
// When a `versionId` is pinned, the query also fetches that version and we swap
// it in as `currentVersion` so every consumer (chip menu, viewer, notes) shows
// the version the model actually read.
export default function useDocReference(
  id: string,
  { skip, versionId }: Options = {},
): DocReferenceResult {
  const { data, loading, error } = useAiDocumentationPreviewQuery({
    variables: {
      id,
      versionId: versionId || '',
      hasVersion: !!versionId,
    },
    skip: skip || !id,
    fetchPolicy: 'cache-first',
  });

  const documentation = data?.data ?? null;
  const { pinnedVersion } = data ?? {};

  return {
    loading,
    error: !!error,
    documentation:
      documentation && pinnedVersion
        ? { ...documentation, currentVersion: pinnedVersion }
        : documentation,
  };
}
